import { Box, Grid, Tabs, Tab, Paper, Typography, Table, TableBody, TableCell, TableHead, TableRow, Autocomplete, TextField, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Home,
  AttachMoney,
  TrendingUp,
  Storage,
  ConfirmationNumber,
  People,
  Memory,
  Groups,
  Widgets,
  RocketLaunch,
  PlayCircle,
  ReportProblem,
  QuestionAnswer,
  Stars,
  CheckCircle,
  RadioButtonUnchecked,
  OpenInFull,
  Close,
  Groups as GroupsIcon,
} from "@mui/icons-material";
import { MonthlyData, MonthlyTrendPoint } from "../types";
import { TOYOTA_RED, INFO_BLUE, SUCCESS_GREEN, WARNING_ORANGE, CHART_COLORS } from "../theme";
import KpiCard from "../components/KpiCard";
import PlotlyChart from "../components/PlotlyChart";
import ChartCard from "../components/ChartCard";
import TableCard from "../components/TableCard";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DualPlatformLayout from "../components/DualPlatformLayout";
import PlatformCost from "./PlatformCost";
import PlatformAdoption from "./PlatformAdoption";
import ProdWorkloads from "./ProdWorkloads";
import TeamProductivity from "./TeamProductivity";



interface Props {
  tdspData: MonthlyData | null;
  enclaveData: MonthlyData | null;
  selectedMonth?: string;
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n}`;
}

function formatCompactHours(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

const MONTH_ORDER: Record<string, number> = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};

function sortCostData(costData: MonthlyData["costData"]) {
  return [...costData].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return (MONTH_ORDER[a.month] ?? 0) - (MONTH_ORDER[b.month] ?? 0);
  });
}

const TAB_KEYS = ["overview", "platform-cost", "adoption", "prod-workloads", "tickets", "users", "key-highlights", "compute-usage", "team-productivity"];

const MonthlyOverview = ({ tdspData, enclaveData, selectedMonth }: Props) => {
  const platformData: Record<string, MonthlyData | null> = { TDSP: tdspData, Enclave: enclaveData };

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = TAB_KEYS.indexOf(searchParams.get("tab") || "");
  const [tab, setTab] = useState(tabFromUrl >= 0 ? tabFromUrl : 0);

  useEffect(() => {
    const urlTab = TAB_KEYS.indexOf(searchParams.get("tab") || "");
    if (urlTab >= 0 && urlTab !== tab) setTab(urlTab);
  }, [searchParams, tab]);

  const handleTabChange = (_: unknown, v: number) => {
    setTab(v);
    setSearchParams({ tab: TAB_KEYS[v] });
  };

  const [selectedAzPod, setSelectedAzPod] = useState<string | null>(null);
  const [selectedAzUser, setSelectedAzUser] = useState<string | null>(null);
  const [expandedPlatform, setExpandedPlatform] = useState<string | null>(null);

  return (
    <Box>
      {/* Navigation Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          border: "1px solid #E8EBF0",
          bgcolor: "#fff",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="standard"
          sx={{
            minHeight: 42,
            height: "auto",
            "& .MuiTabs-scroller": {
              overflow: "visible !important",
            },
            "& .MuiTabs-flexContainer": { flexWrap: "wrap" },
            "& .MuiTabs-scrollButtons": {
              width: 36,
              opacity: 1,
              "&.Mui-disabled": { opacity: 0.3 },
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.78rem",
              color: "#8A92A6",
              minHeight: 42,
              minWidth: "auto",
              px: 1.5,
              py: 0,
              whiteSpace: "nowrap",
              borderBottom: "3px solid transparent",
              "&.Mui-selected, &.Mui-selected:hover": {
                color: "#3B82F6",
                fontWeight: 700,
                borderBottom: "3px solid #3B82F6",
                bgcolor: "transparent",
              },
              "&:hover": { bgcolor: "rgba(59,130,246,0.04)" },
            },
          }}
        >
          <Tab icon={<Home sx={{ fontSize: 18 }} />} iconPosition="start" label="Overview" disableRipple />
          <Tab icon={<AttachMoney sx={{ fontSize: 18 }} />} iconPosition="start" label="Platform Cost" disableRipple />
          <Tab icon={<TrendingUp sx={{ fontSize: 18 }} />} iconPosition="start" label="Adoption" disableRipple />
          <Tab icon={<Storage sx={{ fontSize: 18 }} />} iconPosition="start" label="Prod Workloads" disableRipple />
          <Tab icon={<ConfirmationNumber sx={{ fontSize: 18 }} />} iconPosition="start" label="Tickets" disableRipple />
          <Tab icon={<People sx={{ fontSize: 18 }} />} iconPosition="start" label="Users" disableRipple />
          <Tab icon={<Stars sx={{ fontSize: 18 }} />} iconPosition="start" label="Key Highlights" disableRipple />
          <Tab icon={<Memory sx={{ fontSize: 18 }} />} iconPosition="start" label="Compute Usage" disableRipple />
          <Tab icon={<GroupsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Team Productivity" disableRipple />
        </Tabs>
      </Paper>

      {/* Tab 0: Overview */}
      {tab === 0 && (
        <Box>
          <DualPlatformLayout dataByPlatform={platformData}>
            {(_p, d, single) => {
              const sorted = sortCostData(d.costData);
              const latest = sorted[sorted.length - 1];
              const last6 = sorted.slice(-6);
              const avg = d.sixMonthAvg ?? (last6.reduce((s, r) => s + r.total, 0) / last6.length);
              const uniqueUsers = new Set(d.activeUsers.map((u) => u.user)).size;
              const uniquePods = d.totalPods || new Set(d.activeUsers.map((u) => u.pod)).size;
              return (
              <Box>
                {/* Platform Cost Breakdown */}
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: "#1B2559" }}>
                  Platform Cost Breakdown
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                    <KpiCard label="Databricks" value={formatCompact(latest?.databricks || 0)} tooltip={`$${(latest?.databricks || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} color={INFO_BLUE} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                    <KpiCard label="AWS" value={formatCompact(latest?.aws || 0)} tooltip={`$${(latest?.aws || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} color={TOYOTA_RED} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                    <KpiCard label="Total" value={formatCompact(latest?.total || 0)} tooltip={`$${(latest?.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} color={WARNING_ORANGE} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                    <KpiCard label="6-Month Avg" value={formatCompact(avg)} tooltip={`$${avg.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                    </Box>
                </Box>

                {/* Compute Usage */}
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: "#1B2559" }}>
                  Compute Usage
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                  {d.computeMetrics.totalHours > 0 && (
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <KpiCard label="Total Hours" value={formatCompactHours(d.computeMetrics.totalHours)} tooltip={d.computeMetrics.totalHours.toLocaleString()} color={TOYOTA_RED} />
                    </Box>
                  )}
                  {d.computeMetrics.sagemakerHours > 0 && (
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <KpiCard label="SageMaker" value={formatCompactHours(d.computeMetrics.sagemakerHours)} tooltip={d.computeMetrics.sagemakerHours.toLocaleString()} color={INFO_BLUE} />
                    </Box>
                  )}
                  {d.computeMetrics.ec2Hours > 0 && (
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <KpiCard label="EC2" value={formatCompactHours(d.computeMetrics.ec2Hours)} tooltip={d.computeMetrics.ec2Hours.toLocaleString()} color={TOYOTA_RED} />
                    </Box>
                  )}
                  {d.computeMetrics.airflowHours > 0 && (
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <KpiCard label="Airflow" value={formatCompactHours(d.computeMetrics.airflowHours)} tooltip={d.computeMetrics.airflowHours.toLocaleString()} color={SUCCESS_GREEN} />
                    </Box>
                  )}
                </Box>

                {/* Operational Metrics */}
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: "#1B2559" }}>
                  Operational Metrics
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6} sm={4} lg={single ? 2 : 4}>
                    <KpiCard label="Active Users" value={uniqueUsers} color="#3B82F6" icon={<Groups />} />
                  </Grid>
                  <Grid item xs={6} sm={4} lg={single ? 2 : 4}>
                    <KpiCard label="Active Pods" value={uniquePods} color="#10B981" icon={<Widgets />} />
                  </Grid>
                  <Grid item xs={6} sm={4} lg={single ? 2 : 4}>
                    <KpiCard label="Deployments" value={d.deployments.toLocaleString()} color="#8B5CF6" icon={<RocketLaunch />} />
                  </Grid>
                  <Grid item xs={6} sm={4} lg={single ? 2 : 4}>
                    <KpiCard label="Runs" value={d.runs.toLocaleString()} color="#10B981" icon={<PlayCircle />} />
                  </Grid>
                  <Grid item xs={6} sm={4} lg={single ? 2 : 4}>
                    <KpiCard label="Incidents" value={d.incidents.length} color="#F59E0B" icon={<ReportProblem />} />
                  </Grid>
                  <Grid item xs={6} sm={4} lg={single ? 2 : 4}>
                    <KpiCard label="Requests" value={d.serviceRequests} color="#3B82F6" icon={<QuestionAnswer />} />
                  </Grid>
                </Grid>
              </Box>
              );
            }}
          </DualPlatformLayout>

          {/* 6-Month Trends — only show when a full 6-month history exists:
              the oldest month must have data (platform was active at the start
              of the window), plus at least 3 non-zero months overall. */}
          {(() => {
            const hasMeaningfulTrends = (d: MonthlyData | null) => {
              const trends = d?.monthlyTrends;
              if (!trends || trends.length < 6) return false;
              const isNonZero = (t: MonthlyTrendPoint) =>
                t.users > 0 || t.deployments > 0 || t.runs > 0 || t.incidents > 0 || t.requests > 0;
              const nonZeroMonths = trends.filter(isNonZero);
              return isNonZero(trends[0]) && nonZeroMonths.length >= 3;
            };
            const trendsData: Record<string, MonthlyData | null> = {
              TDSP: hasMeaningfulTrends(platformData.TDSP) ? platformData.TDSP : null,
              Enclave: hasMeaningfulTrends(platformData.Enclave) ? platformData.Enclave : null,
            };
            const hasTrends = trendsData.TDSP || trendsData.Enclave;
            if (!hasTrends) return null;
            return (
              <>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3, mb: 1.5, color: "#1B2559" }}>
                  6-Month Trends
                </Typography>
                <DualPlatformLayout dataByPlatform={trendsData}>
                  {(_p, d, single) => (
              <Grid container spacing={1}>
                <Grid item xs={12} md={single ? 6 : 12}>
                  <ChartCard title="Active Users Trend">
                    <PlotlyChart
                      data={[{
                        x: (d.monthlyTrends || []).map((t) => t.month),
                        y: (d.monthlyTrends || []).map((t) => t.users),
                        type: "scatter",
                        mode: "lines+markers",
                        name: "Active Users",
                        line: { color: TOYOTA_RED, width: 2 },
                        marker: { size: 6 },
                      }]}
                      layout={{}}
                      height={200}
                    />
                  </ChartCard>
                </Grid>
                <Grid item xs={12} md={single ? 6 : 12}>
                  <ChartCard title="Deployments Trend">
                    <PlotlyChart
                      data={[{
                        x: (d.monthlyTrends || []).map((t) => t.month),
                        y: (d.monthlyTrends || []).map((t) => t.deployments),
                        type: "scatter",
                        mode: "lines+markers",
                        name: "Deployments",
                        line: { color: SUCCESS_GREEN, width: 2 },
                        marker: { size: 6 },
                      }]}
                      layout={{}}
                      height={200}
                    />
                  </ChartCard>
                </Grid>
                <Grid item xs={12} md={single ? 6 : 12}>
                  <ChartCard title="Incidents Trend">
                    <PlotlyChart
                      data={[{
                        x: (d.monthlyTrends || []).map((t) => t.month),
                        y: (d.monthlyTrends || []).map((t) => t.incidents),
                        type: "scatter",
                        mode: "lines+markers",
                        name: "Incidents",
                        line: { color: WARNING_ORANGE, width: 2 },
                        marker: { size: 6 },
                      }]}
                      layout={{}}
                      height={200}
                    />
                  </ChartCard>
                </Grid>
                <Grid item xs={12} md={single ? 6 : 12}>
                  <ChartCard title="Runs Trend">
                    <PlotlyChart
                      data={[{
                        x: (d.monthlyTrends || []).map((t) => t.month),
                        y: (d.monthlyTrends || []).map((t) => t.runs),
                        type: "scatter",
                        mode: "lines+markers",
                        name: "Runs",
                        line: { color: TOYOTA_RED, width: 2 },
                        marker: { size: 6 },
                      }]}
                      layout={{}}
                      height={200}
                    />
                  </ChartCard>
                </Grid>
                <Grid item xs={12}>
                  <ChartCard title="Service Requests Trend (6M)">
                    <PlotlyChart
                      data={[{
                        x: (d.monthlyTrends || []).map((t) => {
                          const [y, m] = t.month.split("-");
                          return new Date(+y, +m - 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
                        }),
                        y: (d.monthlyTrends || []).map((t) => t.requests),
                        type: "scatter",
                        mode: "lines+markers",
                        name: "Requests",
                        line: { color: TOYOTA_RED, width: 2 },
                        marker: { size: 6 },
                      }]}
                      layout={{ xaxis: { title: "Month" }, yaxis: { title: "Requests" } }}
                      height={220}
                    />
                  </ChartCard>
                </Grid>
              </Grid>
            )}
          </DualPlatformLayout>
              </>
            );
          })()}
        </Box>
      )}

      {/* Tab 1: Platform Cost */}
      {tab === 1 && (
        <DualPlatformLayout dataByPlatform={platformData}>
          {(_p, d, single) => <PlatformCost data={d} compact platform={_p} />}
        </DualPlatformLayout>
      )}

      {/* Tab 2: Adoption */}
      {tab === 2 && (
        <DualPlatformLayout dataByPlatform={platformData}>
          {(_p, d, single) => <PlatformAdoption data={d} compact />}
        </DualPlatformLayout>
      )}

      {/* Tab 3: Prod Workloads */}
      {tab === 3 && (
        <DualPlatformLayout dataByPlatform={platformData}>
          {(_p, d, single) => <ProdWorkloads data={d} compact />}
        </DualPlatformLayout>
      )}

      {/* Tab 4: Tickets */}
      {tab === 4 && (
        <DualPlatformLayout dataByPlatform={platformData}>
          {(_p, d, single) => (
            <Box>
              <ChartCard title="1TS Ticket Summary by Type">
                <PlotlyChart
                  data={[
                    { x: d.ticketSummary.map((t) => t.type), y: d.ticketSummary.map((t) => t.total), type: "bar", marker: { color: INFO_BLUE }, text: d.ticketSummary.map((t) => String(t.total)), textposition: "auto" },
                  ]}
                  layout={{ xaxis: { title: "Type" }, yaxis: { title: "Count" } }}
                  height={300}
                />
              </ChartCard>
              <Box sx={{ mt: 2 }}>
                <ChartCard title="Incident Classification">
                  <PlotlyChart
                    data={[{
                      labels: ["Access Issues", "Databricks Issues", "SageMaker Issues", "Deployment Issues", "Other"],
                      values: [
                        d.incidents.filter((i) => i.description.toLowerCase().includes("access")).length || 0,
                        d.incidents.filter((i) => i.description.toLowerCase().includes("databricks")).length || 0,
                        d.incidents.filter((i) => i.description.toLowerCase().includes("sagemaker")).length || 0,
                        d.incidents.filter((i) => i.description.toLowerCase().includes("deployment")).length || 0,
                        d.incidents.filter((i) => !["access", "databricks", "sagemaker", "deployment"].some((k) => i.description.toLowerCase().includes(k))).length || d.incidents.length,
                      ],
                      type: "pie",
                      marker: { colors: CHART_COLORS },
                    }]}
                    layout={{}}
                    height={300}
                  />
                </ChartCard>
              </Box>
            </Box>
          )}
        </DualPlatformLayout>
      )}

      {/* Tab 5: Users */}
      {tab === 5 && (
        <DualPlatformLayout dataByPlatform={platformData}>
          {(_p, d, single) => {
            const uniqueUserCount = new Set(d.activeUsers.map((u) => u.user)).size;
            const pods = [...new Set(d.activeUsers.map((u) => u.pod))].sort(
              (a, b) => {
                const aCount = new Set(d.activeUsers.filter((u) => u.pod === a).map((u) => u.user)).size;
                const bCount = new Set(d.activeUsers.filter((u) => u.pod === b).map((u) => u.user)).size;
                return bCount - aCount;
              }
            );
            const podUserCounts = pods.map((p) => new Set(d.activeUsers.filter((u) => u.pod === p).map((u) => u.user)).size);
            const envs = [...new Set(d.activeUsers.map((u) => u.env))];
            const envUserCounts = envs.map((e) => new Set(d.activeUsers.filter((u) => u.env === e).map((u) => u.user)).size);
            return (
            <Box>
              <ChartCard title="Active Users by Pod">
                <PlotlyChart
                  data={[{
                    x: pods,
                    y: podUserCounts,
                    type: "bar",
                    marker: { color: TOYOTA_RED },
                  }]}
                  layout={{ xaxis: { tickangle: -45 } }}
                  height={280}
                />
              </ChartCard>
              <Box sx={{ mt: 2 }}>
                <ChartCard title="User Distribution by Environment">
                  <PlotlyChart
                    data={[{
                      labels: envs,
                      values: envUserCounts,
                      type: "pie",
                      marker: { colors: [TOYOTA_RED, SUCCESS_GREEN, INFO_BLUE] },
                    }]}
                    layout={{}}
                    height={280}
                  />
                </ChartCard>
              </Box>
              <Box sx={{ mt: 2 }}>
                <ChartCard title={`Top 10 Pods by User Count (Total: ${d.azGroupUsers?.totalUsers ?? uniqueUserCount} users)`}>
                  <PlotlyChart
                    data={[{
                      x: (d.azGroupUsers?.topPods ?? pods.slice(0, 10).map((p, i) => ({ pod: p, users: podUserCounts[i] }))).map((p) => p.pod),
                      y: (d.azGroupUsers?.topPods ?? pods.slice(0, 10).map((p, i) => ({ pod: p, users: podUserCounts[i] }))).map((p) => p.users),
                      type: "bar",
                      marker: { color: TOYOTA_RED },
                    }]}
                    layout={{ xaxis: { tickangle: -45 } }}
                    height={280}
                  />
                </ChartCard>
              </Box>

              {d.azGroupUsers?.allPods && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Pod for AZ Group Details:</Typography>
                  <Autocomplete
                    options={d.azGroupUsers.allPods}
                    value={selectedAzPod}
                    onChange={(_, v) => setSelectedAzPod(v)}
                    renderInput={(params) => <TextField {...params} placeholder="Choose an option" size="small" />}
                    sx={{ mb: 2 }}
                  />
                  {selectedAzPod && d.azGroupUsers.membersByPod?.[selectedAzPod] && (
                    <Paper elevation={0} sx={{ border: "1px solid #E8EBF0", borderRadius: 2, overflow: "auto", maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>MemberName</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>role</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>env</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>GroupName</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {d.azGroupUsers.membersByPod[selectedAzPod].map((m, i) => (
                            <TableRow key={i}>
                              <TableCell>{m.member}</TableCell>
                              <TableCell>{m.role ?? ""}</TableCell>
                              <TableCell>{m.env ?? ""}</TableCell>
                              <TableCell>{m.group ?? ""}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  )}
                </Box>
              )}

              {d.azGroupUsers?.allUsers && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Select User to View Groups:</Typography>
                  <Autocomplete
                    options={d.azGroupUsers.allUsers}
                    value={selectedAzUser}
                    onChange={(_, v) => setSelectedAzUser(v)}
                    renderInput={(params) => <TextField {...params} placeholder="Choose an option" size="small" />}
                    sx={{ mb: 2 }}
                  />
                  {selectedAzUser && d.azGroupUsers.membersByUser?.[selectedAzUser] && (
                    <Paper elevation={0} sx={{ border: "1px solid #E8EBF0", borderRadius: 2, overflow: "auto", maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>GroupName</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>role</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>pod</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>env</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {d.azGroupUsers.membersByUser[selectedAzUser].map((g, i) => (
                            <TableRow key={i}>
                              <TableCell>{g.group ?? ""}</TableCell>
                              <TableCell>{g.role ?? ""}</TableCell>
                              <TableCell>{g.pod ?? ""}</TableCell>
                              <TableCell>{g.env ?? ""}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  )}
                </Box>
              )}
            </Box>
            );
          }}
        </DualPlatformLayout>
      )}

      {/* Tab 6: Key Highlights */}
      {tab === 6 && (() => {
        const PLATFORMS_ORDER = ["TDSP", "Enclave"];
        const availablePlatforms = PLATFORMS_ORDER.filter((p) => platformData[p] != null);

        const KNOWN_SECTIONS: Record<string, { title: string; color: string }> = {
          planned: { title: "Planned", color: INFO_BLUE },
          executed: { title: "Executed", color: SUCCESS_GREEN },
          roadmap: { title: "Roadmap", color: WARNING_ORANGE },
        };
        const KNOWN_ORDER = ["planned", "executed", "roadmap"];

        return (
          <Grid container spacing={3}>
            {availablePlatforms.map((platform) => {
              const d = platformData[platform]!;
              const highlights = d.highlights;
              if (!highlights || Array.isArray(highlights)) return null;
              const platformLabel = platform === "TDSP" ? "TDSP" : "Enclave";
              const isSingle = availablePlatforms.length === 1;
              const allKeys = Object.keys(highlights as Record<string, unknown>);
              const orderedKeys = [...KNOWN_ORDER.filter((k) => allKeys.includes(k)), ...allKeys.filter((k) => !KNOWN_ORDER.includes(k))];
              const mdStyles = {
                fontSize: "0.85rem",
                "& h1,& h2,& h3,& h4": { fontSize: "0.9rem", fontWeight: 700, color: "#1B2559", mt: 1.5, mb: 0.5, "&:first-of-type": { mt: 0 } },
                "& p": { m: 0, mb: 0.75, color: "#334155", lineHeight: 1.5 },
                "& ul,& ol": { m: 0, mb: 0.75, pl: 2.5 },
                "& li": { mb: 0.25, color: "#334155", lineHeight: 1.5 },
                "& hr": { border: "none", borderTop: "1px solid #F1F3F7", my: 1.5 },
                "& code": { bgcolor: "#F1F5F9", px: 0.5, borderRadius: 0.5, fontSize: "0.8rem", color: INFO_BLUE },
                "& strong": { fontWeight: 700, color: "#1B2559" },
                "& em": { color: "#64748B" },
              };

              const renderSection = (sectionKey: string, sectionValue: unknown) => {
                if (!sectionValue || (Array.isArray(sectionValue) && (sectionValue as unknown[]).length === 0)) return null;
                const known = KNOWN_SECTIONS[sectionKey];
                const sTitle = known?.title || sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1);
                const sColor = known?.color || CHART_COLORS[Object.keys(KNOWN_SECTIONS).length + orderedKeys.indexOf(sectionKey)] || "#6366F1";
                const sIsExecuted = sectionKey === "executed";
                const sRaw = Array.isArray(sectionValue) ? (sectionValue as string[]).join("\n") : String(sectionValue);
                const sContent = sRaw.replace(/\\n/g, "\n");
                return (
                  <Paper elevation={0} sx={{ p: 0, borderRadius: 3, border: "1px solid #E8EBF0", height: "100%", overflow: "hidden" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, pt: 2, pb: 1 }}>
                      {sIsExecuted ? <CheckCircle sx={{ color: sColor, fontSize: 20 }} /> : <RadioButtonUnchecked sx={{ color: sColor, fontSize: 20 }} />}
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: sColor }}>{sTitle}</Typography>
                    </Box>
                    <Box sx={{ px: 2, pb: 2, pt: 1, ...mdStyles }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{sContent}</ReactMarkdown>
                    </Box>
                  </Paper>
                );
              };

              return (
                <Grid item xs={12} lg={isSingle ? 12 : 6} key={platform}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Stars sx={{ color: INFO_BLUE, fontSize: 22 }} />
                    <Typography variant="h6" fontWeight={700} sx={{ color: "#1B2559", flex: 1 }}>
                      {platformLabel} Initiatives
                    </Typography>
                    <IconButton size="small" onClick={() => setExpandedPlatform(platform)} sx={{ color: "#64748B" }}>
                      <OpenInFull sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ color: "#64748B", mb: 2 }}>
                    Initiatives and progress for the {platformLabel} platform.
                  </Typography>
                  <Grid container spacing={2} alignItems="stretch">
                    {orderedKeys.map((key) => {
                      const value = (highlights as Record<string, unknown>)[key];
                      if (!value || (Array.isArray(value) && value.length === 0)) return null;
                      return (
                        <Grid item xs={12} key={key}>
                          {renderSection(key, value)}
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* Fullscreen dialog */}
                  <Dialog
                    open={expandedPlatform === platform}
                    onClose={() => setExpandedPlatform(null)}
                    fullScreen
                    PaperProps={{ sx: { bgcolor: "#F8FAFC" } }}
                  >
                    <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #E8EBF0", py: 1.5 }}>
                      <Stars sx={{ color: INFO_BLUE, fontSize: 22 }} />
                      <Typography variant="h6" fontWeight={700} sx={{ color: "#1B2559", flex: 1 }}>
                        {platformLabel} Initiatives
                      </Typography>
                      <IconButton onClick={() => setExpandedPlatform(null)}>
                        <Close />
                      </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                      <Grid container spacing={2} alignItems="stretch">
                        {orderedKeys.map((sKey) => {
                          const sVal = (highlights as Record<string, unknown>)[sKey];
                          if (!sVal || (Array.isArray(sVal) && (sVal as unknown[]).length === 0)) return null;
                          return (
                            <Grid item xs={12} md={Math.min(12, Math.floor(12 / orderedKeys.length))} key={sKey}>
                              {renderSection(sKey, sVal)}
                            </Grid>
                          );
                        })}
                      </Grid>
                    </DialogContent>
                  </Dialog>
                </Grid>
              );
            })}
          </Grid>
        );
      })()}

      {/* Tab 7: Compute Usage */}
      {tab === 7 && (
        <DualPlatformLayout dataByPlatform={platformData}>
          {(_p, d, single) => (
            <Box>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <KpiCard label="Total Hours" value={formatCompactHours(d.computeMetrics.totalHours)} tooltip={d.computeMetrics.totalHours.toLocaleString()} color={TOYOTA_RED} icon={<Memory />} />
                </Grid>
                <Grid item xs={6}>
                  <KpiCard label="SageMaker" value={formatCompactHours(d.computeMetrics.sagemakerHours)} tooltip={d.computeMetrics.sagemakerHours.toLocaleString()} color={INFO_BLUE} icon={<Memory />} />
                </Grid>
                <Grid item xs={6}>
                  <KpiCard label="EC2" value={formatCompactHours(d.computeMetrics.ec2Hours)} tooltip={d.computeMetrics.ec2Hours.toLocaleString()} color={TOYOTA_RED} icon={<Memory />} />
                </Grid>
                <Grid item xs={6}>
                  <KpiCard label="Airflow" value={formatCompactHours(d.computeMetrics.airflowHours)} tooltip={d.computeMetrics.airflowHours.toLocaleString()} color={SUCCESS_GREEN} icon={<Memory />} />
                </Grid>
              </Grid>
              <ChartCard title="Compute Hours by Service">
                <PlotlyChart
                  data={[{
                    labels: ["SageMaker", "EC2", "Airflow"],
                    values: [d.computeMetrics.sagemakerHours, d.computeMetrics.ec2Hours, d.computeMetrics.airflowHours],
                    type: "pie",
                    marker: { colors: [INFO_BLUE, TOYOTA_RED, SUCCESS_GREEN] },
                  }]}
                  layout={{}}
                  height={260}
                />
              </ChartCard>
              <Box sx={{ mt: 2 }}>
                <ChartCard title="Compute Hours by Environment">
                  <PlotlyChart
                    data={[
                      { x: d.computeMetrics.byEnv.map((e) => e.env), y: d.computeMetrics.byEnv.map((e) => e.ec2), type: "bar", name: "EC2", marker: { color: TOYOTA_RED } },
                      { x: d.computeMetrics.byEnv.map((e) => e.env), y: d.computeMetrics.byEnv.map((e) => e.sagemaker), type: "bar", name: "SageMaker", marker: { color: INFO_BLUE } },
                      { x: d.computeMetrics.byEnv.map((e) => e.env), y: d.computeMetrics.byEnv.map((e) => e.airflow), type: "bar", name: "Airflow", marker: { color: SUCCESS_GREEN } },
                    ]}
                    layout={{ barmode: "group" }}
                    height={260}
                  />
                </ChartCard>
              </Box>
              <TableCard title="Environment Breakdown" titleColor={TOYOTA_RED} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Env</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }} align="right">EC2</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }} align="right">SageMaker</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }} align="right">Airflow</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }} align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {d.computeMetrics.byEnv.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontSize: "0.75rem" }}>{row.env}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem" }} align="right">{row.ec2.toLocaleString()}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem" }} align="right">{row.sagemaker.toLocaleString()}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem" }} align="right">{row.airflow.toLocaleString()}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem" }} align="right">{(row.ec2 + row.sagemaker + row.airflow).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableCard>
            </Box>
          )}
        </DualPlatformLayout>
      )}

      {/* Tab 8: Team Productivity */}
      {tab === 8 && (
        <DualPlatformLayout dataByPlatform={platformData}>
          {(_p, d, single) => <TeamProductivity data={d} compact={!single} />}
        </DualPlatformLayout>
      )}
    </Box>
  );
};

export default MonthlyOverview;